/**
 * Shared contract vocabulary (spec §11).
 *
 * Every operation is tenant-scoped; every write is idempotent on a client
 * key; requests are strict — unknown fields are refused at the door, not
 * passed through to be someone else's problem.
 */

import { z } from "zod";

export const zId = z.string().min(1);
export const zClientKey = z.string().min(8).max(128);
/**
 * B608 — where mail is SENT: a typed re-delivery destination is an email or
 * it is refused at the door, the same bound every other recipient field
 * carries. One home on purpose: the admin field judge reads THIS schema, so
 * the field and the wire cannot drift (B607's shape).
 */
export const zRecipient = z.string().email().max(320);
/**
 * B689 (INCIDENT TRA-683) — an instant is one a `Date` can hold: ±8.64e15 ms
 * (ECMA-262 §21.4.1.1), not every safe integer. Past that, `new Date(ms)` is
 * Invalid and the first `toISOString` throws a RangeError inside a handler —
 * report.run answered 500 to an eval loop's out-of-range `to`. Refused at the
 * door instead, for every op whose instants become dates.
 */
export const DATE_MAX_EPOCH_MS = 8_640_000_000_000_000;
export const zEpochMs = z.number().int().min(-DATE_MAX_EPOCH_MS).max(DATE_MAX_EPOCH_MS);

/** Integer minor units only — no floats, no exceptions. */
export const zMoney = z
  .object({ amount: z.number().int().safe(), currency: z.string().regex(/^[A-Z]{3}$/) })
  .strict();
export const zNonNegMoney = z
  .object({ amount: z.number().int().safe().nonnegative(), currency: z.string().regex(/^[A-Z]{3}$/) })
  .strict();

/**
 * B20b(h): "public" is the third door — reachable with NO principal (the
 * guest-session mint is how a buyer gets their first credential, so it
 * cannot demand one). Public ops are tenant-scoped, carry no clientKey
 * (each mint is fresh), and their wall is the rate limit + audit row, not
 * a scope.
 */
export type OperationKind = "read" | "write" | "internal" | "public";
/** §11: a human in the loop, named where the spec names one. */
export type Gate = "human" | "admin" | "manager";

/**
 * B25b — the §11 audience column. The permission column's parentheticals
 * ("POS roles", "MANAGER at POS", "ADMIN approves") narrow WHO may call an
 * operation beyond the scope it requires; scopes alone under-specify §11
 * (the payment.record_external finding). Kinds may be role-qualified
 * (STAFF:ADMIN) or device-type-qualified (DEVICE:VALIDATOR); a bare STAFF
 * admits any staff role that also clears the scope wall. DEVICE:TERMINAL is
 * deliberately absent from the vocabulary — §9.1: the provider's terminal
 * fleet never calls the membrane. CAPABILITY (B41) admits saved-capability
 * runs executing under their frozen grant set; no Phase-1 §11 op lists it
 * yet — B42's runtime opens doors deliberately, one op at a time.
 */
export type PrincipalAllowance =
  | "CAPABILITY"
  /** B94 — the platform's own seat. Only ever declared on a `platform` operation. */
  | "OPERATOR"
  | "OPERATOR:ROOT"
  | "OPERATOR:OPERATOR"
  | "OPERATOR:SALES"
  | "OPERATOR:SUPPORT"
  | "CUSTOMER"
  | "RESELLER"
  | "STAFF"
  | "STAFF:ADMIN"
  | "STAFF:MANAGER"
  | "STAFF:SELLER"
  | "STAFF:VALIDATOR"
  | "DEVICE:VALIDATOR"
  | "DEVICE:KIOSK"
  | "DEVICE:PRINTER";

export interface OperationContract<
  Req extends z.ZodTypeAny = z.ZodTypeAny,
  Res extends z.ZodTypeAny = z.ZodTypeAny,
> {
  readonly name: string;
  readonly kind: OperationKind;
  readonly permission: string;
  /** B25b — who may call: the declarative audience the kernel enforces. */
  readonly principals: readonly PrincipalAllowance[];
  readonly gate?: Gate;
  /**
   * B413 — a public door with NO tenant either: the caller is an un-paired
   * device that holds nothing (device.announce). The authorizer and the
   * kernel skip the claimed-tenant wall for exactly these ops; everything
   * else keeps it.
   */
  readonly tenantless?: true;
  /**
   * B94 — the platform door (RULING 2026-09-21, Pathik: a console of our own).
   * The ONE way an OPERATOR principal reaches anything: an operation opts in
   * by declaring which shape of crossing it is, and declares the operator
   * roles it admits in `principals`. Absent — which is every §11 operation —
   * means no operator may call it, enforced by the Inv 10 wall that already
   * exists (an operator holds the platform sentinel, which no request claims).
   *
   * - "fleet": the ask is about the platform, not a tenant (tenant.list).
   *   No claimed tenant; audited on the platform's own partition.
   * - "tenant": the ask names ONE tenant and the operator's `custom:tenants`
   *   must admit it. The audit row is written in THAT TENANT's partition, so
   *   a business reading its own trail sees who looked — which is the whole
   *   of the 2026-09-21 09:59 PDT audit condition.
   *
   * A platform operation declares no `gate`: the §11 gates name staff roles
   * (`gatePasses` refuses every non-STAFF principal by construction), so a
   * gated platform door would be a door that never opens. Walled in
   * `packages/contracts/test/b94-platform-ops.test.ts`.
   */
  readonly platform?: "fleet" | "tenant";
  /**
   * B25c/B25d — the §11 owner rule (RULINGS 2026-07-15 esc 2, 2026-07-22).
   * "customer" marks a CUSTOMER-audience read OR write over customer-owned
   * records: the kernel arms an ownership fence for CUSTOMER principals and
   * the handler MUST discharge it — a foreign-owned id answers exactly like
   * an absent one (uniform 404/empty), never a 200, and never a state-wall
   * refusal that leaks existence. STAFF/RESELLER/DEVICE act across the
   * tenant unaffected. On writes the undischarged-fence 500 records no
   * outcome (the idempotency reservation releases).
   */
  readonly owner?: "customer";
  /**
   * B469 — the tenant entitlement this operation rides behind (docs/
   * TENANT-CUSTOMIZATION.md). Declared as DATA so the kernel's op gate is
   * the ONE seam that checks it; the key must exist in the domain's
   * TENANT_FEATURE_REGISTRY (walled). Absent = the op is for every tenant.
   */
  readonly entitlement?: string;
  readonly request: Req;
  readonly response: Res;
  /** Living documentation: a canonical parsing example per direction. */
  readonly exampleRequest: unknown;
  readonly exampleResponse: unknown;
}

export type RequestOf<C extends OperationContract> = z.infer<C["request"]>;
export type ResponseOf<C extends OperationContract> = z.infer<C["response"]>;

interface BaseOpts<Res extends z.ZodTypeAny> {
  readonly name: string;
  readonly permission: string;
  readonly principals: readonly PrincipalAllowance[];
  readonly fields: z.ZodRawShape;
  readonly response: Res;
  readonly gate?: Gate;
  readonly owner?: "customer";
  readonly entitlement?: string;
  readonly exampleRequest: Record<string, unknown>;
  readonly exampleResponse: unknown;
}

const EXAMPLE_TENANT = "tenant-1";
const EXAMPLE_KEY = "client-key-0001";

function build<Res extends z.ZodTypeAny>(
  kind: OperationKind,
  shape: z.ZodRawShape,
  opts: BaseOpts<Res>,
  exampleExtras: Record<string, unknown>,
): OperationContract<z.ZodTypeAny, Res> {
  if ((kind === "internal" || kind === "public") && opts.principals.length > 0)
    throw new Error(`${kind} operation ${opts.name} cannot declare an external audience`);
  if (kind !== "internal" && kind !== "public" && opts.principals.length === 0)
    throw new Error(`Operation ${opts.name} must declare its audience (B25b)`);
  if (opts.owner !== undefined && !opts.principals.includes("CUSTOMER"))
    throw new Error(`Operation ${opts.name} declares an owner rule but admits no CUSTOMER (B25c)`);
  const contract: OperationContract<z.ZodTypeAny, Res> = {
    name: opts.name,
    kind,
    permission: opts.permission,
    principals: Object.freeze([...opts.principals]),
    ...(opts.gate !== undefined ? { gate: opts.gate } : {}),
    ...(opts.owner !== undefined ? { owner: opts.owner } : {}),
    ...(opts.entitlement !== undefined ? { entitlement: opts.entitlement } : {}),
    request: z.object(shape).strict(),
    response: opts.response,
    exampleRequest: { ...exampleExtras, ...opts.exampleRequest },
    exampleResponse: opts.exampleResponse,
  };
  return contract;
}

/** A read: tenant-scoped, no client key — reads have nothing to replay. */
export function readOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  return build("read", { tenantId: zId, ...opts.fields }, opts, { tenantId: EXAMPLE_TENANT });
}

/** A write: tenant-scoped and idempotent on a client key (Inv 4). */
export function writeOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  return build("write", { tenantId: zId, clientKey: zClientKey, ...opts.fields }, opts, {
    tenantId: EXAMPLE_TENANT,
    clientKey: EXAMPLE_KEY,
  });
}

/**
 * B20b(h): the public door — tenant-scoped, principal-less, no client key.
 * The kernel skips the principal walls for this kind; the handler's rate
 * limit and the membrane's audit row are the walls.
 */
export function publicOp<Res extends z.ZodTypeAny>(opts: Omit<BaseOpts<Res>, "principals">) {
  return build("public", { tenantId: zId, ...opts.fields }, { ...opts, principals: [] }, { tenantId: EXAMPLE_TENANT });
}

/**
 * B94 — a PLATFORM read across the fleet (tenant.list). No tenantId: the
 * ask is about the platform, not about one business, and the operator's
 * own `custom:tenants` scope is what bounds the answer — applied in the
 * handler, over rows the directory already holds. Only OPERATOR principals
 * reach it (walled in `test/b94-platform-ops.test.ts`).
 */
export function fleetReadOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("read", { ...opts.fields }, opts, {});
  return Object.freeze({ ...contract, platform: "fleet" as const });
}

/**
 * B94 leg (c) — a PLATFORM write across the fleet (operator.users.*). No
 * tenantId — the record it writes belongs to the platform itself — but the
 * clientKey stays: a fleet write is as replayable as a tenant one (Inv 4;
 * the kernel partitions its idempotency row under the operator sentinel).
 */
export function fleetWriteOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("write", { clientKey: zClientKey, ...opts.fields }, opts, { clientKey: EXAMPLE_KEY });
  return Object.freeze({ ...contract, platform: "fleet" as const });
}

/**
 * B94 — a PLATFORM read or write INTO one named tenant. The tenant is named
 * in the request as usual, but the wall is the operator's scope rather than
 * the Inv 10 equality, and the audit row lands in that tenant's own trail.
 */
export function tenantCrossingReadOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("read", { tenantId: zId, ...opts.fields }, opts, { tenantId: EXAMPLE_TENANT });
  return Object.freeze({ ...contract, platform: "tenant" as const });
}

/**
 * B94 leg (d) — a PLATFORM write INTO one named tenant (`operator.login_as`):
 * the crossing wall of tenantCrossingReadOp, plus the client key every
 * write carries (Inv 4).
 */
export function tenantCrossingWriteOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("write", { tenantId: zId, clientKey: zClientKey, ...opts.fields }, opts, {
    tenantId: EXAMPLE_TENANT,
    clientKey: EXAMPLE_KEY,
  });
  return Object.freeze({ ...contract, platform: "tenant" as const });
}

/**
 * B413 — the tenant-LESS public door: no principal AND no tenant. The one
 * caller shape is an un-paired device announcing its pairing code; the
 * tenant "arrives inside the enrolled credential" (validator config), so
 * demanding one here would demand what the caller cannot know. The
 * handler's rate wall is load-bearing — a tenantless door has no other.
 */
export function publicTenantlessOp<Res extends z.ZodTypeAny>(opts: Omit<BaseOpts<Res>, "principals">) {
  const contract = build("public", { ...opts.fields }, { ...opts, principals: [] }, {});
  return Object.freeze({ ...contract, tenantless: true as const });
}

/** Provider-facing: exactly-once on the provider's event id, never a client key. */
export function internalOp<Res extends z.ZodTypeAny>(opts: Omit<BaseOpts<Res>, "principals">) {
  return build("internal", { tenantId: zId, ...opts.fields }, { ...opts, principals: [] }, { tenantId: EXAMPLE_TENANT });
}

/**
 * B551 leg (b) — the reference id of a failed call: the failure body's
 * `requestId` (every ops-entry failure carries it), else the API Gateway
 * `apigw-requestid` header the ops API exposes (an answer the gateway made
 * itself — a throttle, a 503 — has no body of ours). Every app's API error
 * reads it here, so the rule lives once.
 */
export function wireRequestId(body: unknown, header: string | null | undefined): string | undefined {
  if (body !== null && typeof body === "object") {
    const id = (body as { requestId?: unknown }).requestId;
    if (typeof id === "string" && id !== "") return id.slice(0, 128);
  }
  return typeof header === "string" && header !== "" ? header.slice(0, 128) : undefined;
}
